import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { removeProductAsGiftWithPurchaseRequest } from "../../actions/ShopActions";
import { getImageAltText, sanitizeMarkup } from "../../utils/shop";
import { getCheckoutId, getShopState } from "../../utils/reduxSelectors";
import { EVENT_PROPERTIES_VALUES } from "../../types/Amplitude";

// Components
import IconButton from "../Atoms/IconButton";
import Image from "../Atoms/Image";
import LoadingSpinner from "../Atoms/LoadingSpinner";
import { TrackingContext } from "../Utils/TrackingWrapper";
import { ImgixImage } from "../Utils/ImgixImage";

// Constants
import { IMGIX_BASE_PATH } from "../../utils/constants";

// Stylesheets
import "./LineItem.scss";

export const RemoveGWPLineItem = ({
  lineItem,
  checkoutId,
  baseTrackingProperties,
  trackRemoveItemFromCart,
  gwpProductKey,
}) => {
  const dispatch = useDispatch();
  const removeProductAsGWPAction = () => {
    trackRemoveItemFromCart({
      ...baseTrackingProperties,
      currentQuantity: 0,
      action: EVENT_PROPERTIES_VALUES.Decrease_Quantity_Icon_Clicked,
    });

    dispatch(
      removeProductAsGiftWithPurchaseRequest({
        checkoutId,
        lineItem,
        productKey: gwpProductKey,
      })
    );
  };

  return (
    <IconButton
      className="item_remove_button"
      ariaLabel="Remove Item"
      onClick={removeProductAsGWPAction}
    >
      <Image src={`${IMGIX_BASE_PATH}/remove-item.svg`} alt="Remove item" />
    </IconButton>
  );
};

const renderLoadingSpinner = (lineItemId, quantityUpdates) => {
  if (quantityUpdates.includes(lineItemId)) {
    return <LoadingSpinner color="blue" />;
  }

  return null;
};

export const renderDescription = (description) => {
  // If item is a variant, render the description which contains the variant name
  if (description) {
    return (
      <span
        className="caption_secondary variant_info"
        dangerouslySetInnerHTML={{ __html: sanitizeMarkup(description) }}
      />
    );
  }

  return null;
};

export const LineItemGiftWithPurchase = ({ lineItem, gwpProductKey }) => {
  const {
    trackingActions: { trackRemoveItemFromCart },
    pageUrl,
  } = useContext(TrackingContext);
  const checkoutId = useSelector((state) => getCheckoutId(state), shallowEqual);
  const { quantityUpdates } = useSelector(
    (state) => getShopState(state),
    shallowEqual
  );

  return (
    <li className="line_item_gwp line_item">
      {renderLoadingSpinner(lineItem.id, quantityUpdates)}

      <div className="product_image_container">
        <ImgixImage
          src={lineItem.image.src}
          alt={getImageAltText({
            altText: lineItem.image.altText,
            productName: lineItem.productName,
            variantName: lineItem.variantName,
          })}
          width={96}
          height={96}
          isShopify
          className="product_image"
        />
      </div>

      <div className="line_item_content">
        <div className="product_info">
          <header className="header">
            <h3 className="h6_text product_title">{lineItem.displayName}</h3>

            {renderDescription(lineItem.description)}
          </header>

          <RemoveGWPLineItem
            checkoutId={checkoutId}
            lineItem={lineItem}
            trackRemoveItemFromCart={trackRemoveItemFromCart}
            baseTrackingProperties={{
              product: lineItem.productName,
              variant: lineItem.variantName,
              sectionName: EVENT_PROPERTIES_VALUES.Cart,
              pageUrl,
              lineItem,
            }}
            gwpProductKey={gwpProductKey}
          />
        </div>

        <footer className="line_item_footer">
          <span className="h6_text price" data-testid="price">
            FREE Gift (Qty: {lineItem.quantity})
          </span>
        </footer>
      </div>
    </li>
  );
};

LineItemGiftWithPurchase.propTypes = {
  lineItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    variantId: PropTypes.string.isRequired,
    description: PropTypes.string,
    promotionalMessage: PropTypes.string,
    variantName: PropTypes.string,
    quantity: PropTypes.number.isRequired,
    productName: PropTypes.string.isRequired,
    image: PropTypes.object.isRequired,
    displayName: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      .isRequired,
  }).isRequired,
  gwpProductKey: PropTypes.string.isRequired,
};

export default LineItemGiftWithPurchase;
